import React, { useEffect, useState } from "react";
import { useAuth } from "@/hooks/useAuth";
import { supabase } from "@/lib/supabase";
import { motion } from "framer-motion";
import UnreadMessagesAlert from "@/components/messaging/UnreadMessagesAlert";
import QuickActions from "@/components/layout/QuickActions";
import { Home } from "lucide-react";
import { Separator } from "@/components/ui/separator";

interface DashboardHeaderProps {
  className?: string;
}

const DashboardHeader: React.FC<DashboardHeaderProps> = ({
  className
}) => {
  const {
    user,
    organizationId
  } = useAuth();
  const [hiveLogo, setHiveLogo] = useState<string | null>(null);
  const [firstName, setFirstName] = useState<string>('Admin');
  const [studioName, setStudioName] = useState<string>('Your Organization');

  useEffect(() => {
    const fetchUserProfile = async () => {
      if (!user) return;
      try {
        const userMeta = user.user_metadata;
        if (userMeta && userMeta.first_name) {
          setFirstName(userMeta.first_name);
        } else if (userMeta && userMeta.name) {
          setFirstName(userMeta.name.split(' ')[0]);
        } else if (user.email) {
          setFirstName(user.email.split('@')[0] || 'Admin');
        }
      } catch (error) {
        console.error("Error fetching user profile:", error);
        if (user.email) {
          setFirstName(user.email.split('@')[0] || 'Admin');
        }
      }
    };

    const fetchOrganizationData = async () => {
      try {
        if (!organizationId) {
          console.error("No organization ID found");
          return;
        }
        const {
          data,
          error
        } = await supabase.from('organizations').select('logo_url, name').eq('id', organizationId).single();
        if (error) throw error;
        if (data && 'name' in data) {
          setStudioName(data.name as string || 'Your Organization');
        }
        if (data && 'logo_url' in data) {
          setHiveLogo(data.logo_url as string | null);
        }
      } catch (error) {
        console.error("Error fetching organization data:", error);
        setStudioName('Your Organization');
      }
    };

    fetchUserProfile();
    fetchOrganizationData();
  }, [user, organizationId]);

  return (
    <motion.div 
      className="dashboard-header flex flex-col gap-4 w-full" 
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.6, ease: [0.22, 1, 0.36, 1] }}
    >
      {/* Organization info section */}
      <div className="flex items-center gap-4">
        <div className="bg-[#4ba3c7] p-4 rounded-full flex items-center justify-center">
          <Home className="h-6 w-6 text-white" />
        </div>
        <div className="flex-1 min-w-0">
          <h1 className="text-2xl font-bold tracking-tight text-[#4ba3c7] text-left truncate">{studioName}</h1>
        </div>
        <div className="flex items-center gap-3">
          <UnreadMessagesAlert />
        </div>
      </div>
      
      {/* Quick Actions section - ensure visibility */}
      <div className="flex justify-end w-full overflow-x-auto">
        <QuickActions />
      </div>
      
      <Separator variant="gradient" />
    </motion.div>
  );
};

export default DashboardHeader;